import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import axiosInstance from './axiosInstance';
import {
  AdminSetPasswordDto,
  SecretaryCreationDto,
  SecretaryResponseDto,
  SecretarySetPasswordResDto,
  SecretaryUpdateDto,
} from '@docbay/schemas';
import axiosAuthInstance from './axiosAuthInstance';

/**
 * SecretariesAPI is an object containing methods for interacting with the secretaries API.
 */
export const SecretariesAPI = {
  /**
   * createSecretary creates a new secretary with the given data.
   * @param {SecretaryCreationDto} data - The data to use to create the secretary.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  createSecretary: (data: SecretaryCreationDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post('/secretaries', { ...data, language });
  },

  /**
   * getSecretaryById retrieves a secretary by their ID.
   * @param {string} id - The ID of the secretary to retrieve.
   * @returns {Promise<SecretaryResponseDto>} A promise that resolves to the retrieved secretary.
   */
  getSecretaryById: (id: string) => {
    const params = {
      include: 'photo,clinicProfessionals,clinicOwner',
    };

    return axiosInstance.get<SecretaryResponseDto>(`/secretaries/${id}`, {
      params,
    });
  },

  /**
   * editSecretary edits a secretary with the given ID and data.
   * @param {string} id - The ID of the secretary to edit.
   * @param {SecretaryUpdateDto} data - The data to use to update the secretary.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  editSecretary: (id: string, data: SecretaryUpdateDto) =>
    axiosInstance.patch(`/secretaries/${id}`, data),

  /**
   * deleteSecretary deletes a secretary with the given ID.
   * @param {string} id - The ID of the secretary to delete.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  deleteSecretary: (id: string) => axiosInstance.delete(`/secretaries/${id}`),

  /**
   * resendSecretariesAuthorizationData resends authorization data for a secretary with the given ID.
   * @param {string} id - The ID of the secretary to resend authorization data for.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  resendSecretariesAuthorizationData: (id: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post(`/secretaries/${id}/resend-authorization-data`, {
      language,
    });
  },

  /**
   * checkSecretariesVerificationCode checks a verification code for a secretary with the given email.
   * @param {string} email - The email of the secretary to check the verification code for.
   * @param {string} code - The verification code to check.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  checkSecretariesVerificationCode: (email: string, code: string) =>
    axiosAuthInstance('').post('/secretaries/verification-code', {
      email,
      code,
    }),

  /**
   * createSecretariesPassword creates a password for a secretary with the given data.
   * @param {AdminSetPasswordDto} data - The data to use to create the password.
   * @returns {Promise<SecretarySetPasswordResDto>} A promise that resolves to the response from the API.
   */
  createSecretariesPassword: (data: AdminSetPasswordDto) =>
    axiosAuthInstance('').post<SecretarySetPasswordResDto>(
      '/secretaries/password',
      data,
    ),

  /**
   * changeSecretariesEmail changes the email for a secretary with the given ID.
   * @param {string} id - The ID of the secretary to change the email for.
   * @param {string} email - The new email to set for the secretary.
   * @param {string} userType - The user type of the secretary.
   * @returns {Promise<any>} A promise that resolves to the response from the API.
   */
  changeSecretariesEmail: (id: string, email: string, userType: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.patch(`/secretaries/${id}/email`, {
      email,
      language,
      userType,
    });
  },
};
